/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { format } from 'date-fns';
/**
 * Internal Imports
 */
import SearchHeader from '../../../components/searchHeader';
import EmployeeRoundsReportTable from '../../../components/customTables/reports/employee/roundsReportTable';
import { RootState } from '../../../redux/store';
import {
  clearEmployeeRoundsReport,
  clearRoundsReportFilter,
  getEmployeeRoundsReport,
  getEmployeeRoundsReportDownload
} from '../../../redux/actions';

import { date } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  EmpDepartment,
  EmpRoundsReport,
  Filter
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ReportContainer } from '../styles/ReportsViews.styles';

interface RoundsReportProps {
  authorizedUser: AuthUserProps;
  allEmpDepartments: EmpDepartment[];
  currentEmployeeRoundsReportPage: number;
  employeeRoundsReport: EmpRoundsReport[];
  reportSearchQuery: string;
  roundsReportFilter: Filter;
  searchTerm: string;
  selectedEmpDepartments: EmpDepartment[];
  sortKey: string;
  sortDir: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const RoundsReport: FC<RoundsReportProps> = ({
  authorizedUser,
  allEmpDepartments,
  currentEmployeeRoundsReportPage,
  employeeRoundsReport,
  reportSearchQuery,
  roundsReportFilter,
  searchTerm,
  selectedEmpDepartments,
  sortKey,
  sortDir,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const today = new Date();
  const dateRange = {
    startDate: format(
      new Date(today.getFullYear(), today.getMonth(), 1),
      'yyyy-MM-dd'
    ),
    endDate: format(today, 'yyyy-MM-dd')
  };

  useEffect(() => {
    return () => {
      dispatch(clearRoundsReportFilter());
      dispatch(clearEmployeeRoundsReport());
    };
  }, []);

  useEffect(() => {
    dispatch(
      getEmployeeRoundsReport({
        facilityId: authorizedUser?.facilityId,
        query: reportSearchQuery || '',
        roundedBy: roundsReportFilter.employeeId || '',
        templateName: roundsReportFilter.template || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        departments:
          selectedEmpDepartments.length > 0
            ? selectedEmpDepartments.length === allEmpDepartments.length
              ? 'ALL'
              : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
            : '',
        startday: date.formatFilterStartDate(
          roundsReportFilter.startDate || dateRange.startDate
        ),
        endday: date.formatFilterEndDate(
          roundsReportFilter.endDate || dateRange.endDate
        ),
        page: currentEmployeeRoundsReportPage
      })
    );
  }, [
    authorizedUser?.facilityId,
    reportSearchQuery,
    roundsReportFilter,
    sortKey,
    sortDir,
    currentEmployeeRoundsReportPage,
    selectedEmpDepartments
  ]);

  const listToDisplay = useMemo(() => {
    if (employeeRoundsReport?.length) {
      const cleanData = employeeRoundsReport.map((round: EmpRoundsReport) => ({
        ...round,
        department: round?.employeeDept || '',
        employee:
          `${round?.employeeFirstName} ${round?.employeeLastName}` || '',
        roundedBy:
          round?.complianceStatus === 'notrounded'
            ? ''
            : `${round?.userFirstName} ${round?.userLastName}`,
        createdUserId: round?.userId || '',
        date: round?.roundDate || '',
        status:
          round?.complianceStatus === 'compliant'
            ? 'Compliant'
            : round?.complianceStatus === 'notrounded'
            ? 'Not Rounded'
            : 'Rounded',
        templateName:
          round?.complianceStatus === 'notrounded' ? '' : round?.templateName
      }));

      return cleanData;
    }
    return [];
  }, [employeeRoundsReport, selectedEmpDepartments]);

  const handleExportCsv = useCallback(() => {
    dispatch(
      getEmployeeRoundsReportDownload({
        facilityId: authorizedUser?.facilityId,
        query: reportSearchQuery || '',
        roundedBy: roundsReportFilter.employeeId || '',
        templateName: roundsReportFilter.template || '',
        departments:
          selectedEmpDepartments.length === allEmpDepartments.length
            ? 'ALL'
            : selectedEmpDepartments.map(dept => dept.departmentId).join(','),
        sortBy: sortKey,
        sortOrder: sortDir,
        startday: date.formatFilterStartDate(
          roundsReportFilter.startDate || dateRange.startDate
        ),
        endday: date.formatFilterEndDate(
          roundsReportFilter.endDate || dateRange.endDate
        )
      })
    );
  }, [
    authorizedUser?.facilityId,
    reportSearchQuery,
    roundsReportFilter,
    selectedEmpDepartments,
    sortKey,
    sortDir
  ]);

  const handleClickDownloadPDF = useCallback(async () => {
    dispatch(
      getEmployeeRoundsReportDownload({
        facilityId: authorizedUser?.facilityId,
        query: reportSearchQuery || '',
        roundedBy: roundsReportFilter.employeeId || '',
        templateName: roundsReportFilter.template || '',
        departments:
          selectedEmpDepartments.length === allEmpDepartments.length
            ? 'ALL'
            : selectedEmpDepartments.map(dept => dept.departmentId).join(','),
        sortBy: sortKey,
        sortOrder: sortDir,
        startday: date.formatFilterStartDate(
          roundsReportFilter.startDate || dateRange.startDate
        ),
        endday: date.formatFilterEndDate(
          roundsReportFilter.endDate || dateRange.endDate
        ),
        isDownload: true
      })
    );
  }, [
    authorizedUser?.facilityId,
    reportSearchQuery,
    roundsReportFilter,
    selectedEmpDepartments,
    sortKey,
    sortDir
  ]);

  return (
    <ReportContainer id="Employee-Rounds-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <EmployeeRoundsReportTable roundsReportList={listToDisplay} />
    </ReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser,
  currentEmployeeRoundsReportPage:
    state.ReportReducer.currentEmployeeRoundsReportPage,
  employeeRoundsReport: state.ReportReducer.employeeRoundsReport,
  reportSearchQuery: state.ReportReducer.reportSearchQuery,
  roundsReportFilter: state.ReportReducer.roundsReportFilter,
  sortKey: state.ReportReducer.sortRoundsKey,
  sortDir: state.ReportReducer.sortRoundsDir,
  selectedEmpDepartments: state.ConfigReducer.selectedEmpDepartments,
  allEmpDepartments: state.ConfigReducer.empDepartments
});

export default connect(mapReduxStateToProps)(RoundsReport);
